import { ChangeEventHandler, FC, MouseEventHandler, useState } from 'react';

import { FileUpload, Input, PrimaryButton } from '@cb/apricot-react';
import { Grid } from '@mui/material';
import { ISummary, TAIForm } from 'src/types';

import Typography from './Typography';
import { FileUploadFieldProps } from './FileUploadField';
import AttachmentList from './AttachmentList';

export interface FileUploaderProps extends Omit<FileUploadFieldProps, 'path'> {
  data?: Nullable<ISummary | TAIForm>;
  errorMessage?: string;
  hasError?: boolean;
  path?: string;
}

const FileUploader: FC<FileUploaderProps> = ({
  acceptFileTypes,
  data,
  errorMessage,
  hasError,
  uploadButton,
  label,
  path,
  readOnly,
  required,
  onUploadFile,
}) => {
  const [click, setClick] = useState(0);
  const [loading, setLoading] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [description, setDescription] = useState('');

  const handleClick: MouseEventHandler = (event) => {
    if (click % 2) {
      event.preventDefault();
    }
    setClick((click) => click + 1);
  };

  const handleFileRemoved = () => {
    setFile(null);
  };

  const handleFileSelected = (event: any) => {
    setFile(event.data.file);
  };

  const handleChangeDescription: ChangeEventHandler<HTMLInputElement> = (event) => {
    setDescription(event.target.value);
  };

  const handleUploadFile = async () => {
    if (!file || !onUploadFile) {
      return;
    }

    try {
      setLoading(true);
      await onUploadFile(file, description);
      setFile(null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Grid container>
      {label && (
        <Grid item xs={12}>
          <Typography hasError={hasError} variant="body1">{`${label} ${required ? '*' : ''}`}</Typography>
        </Grid>
      )}

      {errorMessage && (
        <Grid item xs={12}>
          <Typography hasError={hasError} variant="body1">
            {errorMessage}
          </Typography>
        </Grid>
      )}

      {data && <AttachmentList data={data} fieldPath={path} />}

      <Grid item xs={12}>
        <FileUpload
          id="fileUpload"
          label={uploadButton ?? 'Add picture'}
          // @ts-ignore
          accept={acceptFileTypes ?? 'image/*,application/pdf'}
          disabled={readOnly ?? loading}
          onClick={handleClick}
          onFileRemoved={handleFileRemoved}
          onFileSelected={handleFileSelected}
        />
      </Grid>

      {file ? (
        <div className="display-flex justify-content-xs-start align-items-xs-end">
          <Input
            clearable
            floating={false}
            label="Description"
            value={description}
            onChange={handleChangeDescription}
          />

          <PrimaryButton disabled={loading} style={{ marginLeft: 8 }} onClick={handleUploadFile}>
            Upload
          </PrimaryButton>
        </div>
      ) : null}
    </Grid>
  );
};

export default FileUploader;
